// src/features/resources/components/ResourceList.js
import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Box,
  Divider,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Button
} from "@mui/material";
import {
  PictureAsPdf as PdfIcon,
  Movie as VideoIcon,
  Audiotrack as AudioIcon,
  Link as LinkIcon,
  MoreVert as MoreVertIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Folder as FolderIcon,
  Download as DownloadIcon
} from "@mui/icons-material";
import { fetchAllResources, deleteResource } from "../../services/resourceService";
import { fetchFolderById } from "../../services/folderService";
import { useAuth } from "../../contexts/AuthContext";

const ICON_MAP = {
  pdf: <PdfIcon sx={{ color: "#E53935" }} />,
  video: <VideoIcon sx={{ color: "#673AB7" }} />,
  audio: <AudioIcon sx={{ color: "#3F51B5" }} />,
  youtube: <VideoIcon sx={{ color: "#FF0000" }} />,
  link: <LinkIcon />
};

const ResourceList = ({ onEdit }) => {
  const [resources, setResources] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuFor, setMenuFor] = useState(null);
  const { currentUser, userProfile } = useAuth();

  const load = async () => {
    const all = await fetchAllResources();
    setResources(all || []);
  };

  useEffect(() => {
    load();
  }, []);

  const handleOpenMenu = (event, resource) => {
    setAnchorEl(event.currentTarget);
    setMenuFor(resource);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuFor(null);
  };

  const handleDelete = async (resource) => {
    if (!window.confirm("Delete this resource? This will remove metadata and file (if uploaded).")) return;
    try {
      await deleteResource(resource.id);
      await load();
    } catch (err) {
      console.error("Delete failed", err);
      alert("Failed to delete resource.");
    } finally {
      handleCloseMenu();
    }
  };

  const safeType = (t) => (t ? t.toLowerCase() : "link");
  const safeTitle = (r) => r.title || "Untitled resource";

  return (
    <Card sx={{ maxWidth: 900, mx: "auto", mt: 4 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h6">Resources</Typography>
            <Typography variant="caption" color="textSecondary">Browse and manage resources</Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <List>
          {resources.length === 0 ? (
            <Box textAlign="center" p={4}>
              <Typography color="textSecondary">No resources yet.</Typography>
            </Box>
          ) : (
            resources.map((r) => (
              <React.Fragment key={r.id}>
                <ListItem
                  secondaryAction={
                    <>
                      <IconButton href={r.fileUrl || "#"} target="_blank" title="Open">
                        <OpenInNewIcon />
                      </IconButton>
                      <IconButton onClick={(e) => handleOpenMenu(e, r)}><MoreVertIcon /></IconButton>
                    </>
                  }
                >
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: "transparent" }}>
                      {ICON_MAP[safeType(r.type)] || <LinkIcon />}
                    </Avatar>
                  </ListItemIcon>

                  <ListItemText
                    primary={safeTitle(r)}
                    secondary={`${r.subjectId ? r.subjectId + " • " : ""}${r.grade ? "Grade " + r.grade + " • " : ""}${(r.visibility || "private").toUpperCase()}`}
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))
          )}
        </List>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleCloseMenu}
        >
          <MenuItem
            onClick={() => {
              // open link in new tab (menuFor)
              if (menuFor?.fileUrl) window.open(menuFor.fileUrl, "_blank");
              handleCloseMenu();
            }}
          >
            <ListItemIcon><OpenInNewIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Open</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              if (onEdit && menuFor) onEdit(menuFor.id);
              handleCloseMenu();
            }}
          >
            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              if (menuFor?.fileUrl) {
                // trigger download
                const a = document.createElement("a");
                a.href = menuFor.fileUrl;
                a.download = menuFor.title || "resource";
                document.body.appendChild(a);
                a.click();
                a.remove();
              }
              handleCloseMenu();
            }}
          >
            <ListItemIcon><DownloadIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Download</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleDelete(menuFor);
            }}
          >
            <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>
      </CardContent>
    </Card>
  );
};

export default ResourceList;
